import React, { useState, useContext } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  Typography,
  makeStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import SaveIcon from '@material-ui/icons/Save';
import { AppContext } from '../../AppContext';
import {
  createSelfInspectImageConfig,
  updateSelfInspectImageConfig,
  deleteSelfInspectImageConfig,
} from '../../services/systemConfiguration.service';

const useStyles = makeStyles((theme) => ({
  card: {
    height: '100%',
    width: '100%',
  },
  cardHeader: {
    padding: '14px 18px',
    backgroundColor: '#f0f0f0',
    borderBottom: '1px solid #e0e0e0',
  },
  cardHeaderTitle: {
    fontSize: '1.3rem',
    fontWeight: 700,
    color: 'var(--jmi-blue)',
    letterSpacing: '0.01em',
  },
  cardContent: {
    padding: 16,
    '&:last-child': { paddingBottom: 16 },
  },
  cardActions: {
    padding: '8px 16px',
    justifyContent: 'space-between',
    borderTop: '1px solid #e0e0e0',
  },
  scopeSelect: {
    width: '100%',
  },
  valueSelect: {
    width: '100%',
  },
  numberInput: {
    width: '100%',
  },
  unsaved: {
    fontSize: 12,
    color: theme.palette.warning.main,
  },
}));

const SCOPE_PROPERTY_TYPE = 'propertyType';
const SCOPE_PRODUCT = 'product';

const emptyRow = () => ({
  _key: Math.random(),
  selfInspectImageConfigId: 0,
  scope: SCOPE_PROPERTY_TYPE,
  propertyTypeId: '',
  productId: '',
  minImageCount: 4,
});

export const ImageCountConfig = ({ configs, propertyTypes, products, onRefresh }) => {
  const classes = useStyles();
  const { dispatch } = useContext(AppContext);
  const [rows, setRows] = useState(
    configs.map((c) => ({
      ...c,
      _key: c.selfInspectImageConfigId,
      scope: c.productId ? SCOPE_PRODUCT : SCOPE_PROPERTY_TYPE,
    }))
  );
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const notify = (message, variant = 'success') =>
    dispatch({ type: 'SET_SNACKBAR', snackbar: { isOpen: true, message, variant } });

  const update = (index, changes) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], ...changes };
    setRows(updated);
    setDirty(true);
  };

  const handleScopeChange = (index, scope) =>
    update(index, { scope, propertyTypeId: '', productId: '' });

  const handleAddRow = () => {
    setRows([...rows, emptyRow()]);
    setDirty(true);
  };

  const handleDelete = async (index) => {
    const row = rows[index];
    if (row.selfInspectImageConfigId > 0) {
      try {
        await deleteSelfInspectImageConfig(row.selfInspectImageConfigId);
        notify('Configuration deleted');
        onRefresh();
      } catch {
        notify('Failed to delete configuration', 'error');
      }
    } else {
      setRows(rows.filter((_, i) => i !== index));
    }
  };

  const handleSave = async () => {
    for (const row of rows) {
      if (row.scope === SCOPE_PROPERTY_TYPE && !row.propertyTypeId) {
        notify('Select a property type for every row', 'warning');
        return;
      }
      if (row.scope === SCOPE_PRODUCT && !row.productId) {
        notify('Select a product for every row', 'warning');
        return;
      }
      if (!row.minImageCount || row.minImageCount < 1) {
        notify('Minimum image count must be at least 1', 'warning');
        return;
      }
    }

    setSaving(true);
    try {
      for (const row of rows) {
        const payload = {
          selfInspectImageConfigId: row.selfInspectImageConfigId,
          propertyTypeId: row.scope === SCOPE_PROPERTY_TYPE ? row.propertyTypeId || null : null,
          productId: row.scope === SCOPE_PRODUCT ? row.productId || null : null,
          minImageCount: Number(row.minImageCount),
        };
        if (row.selfInspectImageConfigId > 0) {
          await updateSelfInspectImageConfig(row.selfInspectImageConfigId, payload);
        } else {
          await createSelfInspectImageConfig(payload);
        }
      }
      notify('Image count configurations saved');
      setDirty(false);
      onRefresh();
    } catch {
      notify('Failed to save configurations', 'error');
    } finally {
      setSaving(false);
    }
  };

  const usedPropertyTypeIds = rows
    .filter((r) => r.scope === SCOPE_PROPERTY_TYPE)
    .map((r) => r.propertyTypeId)
    .filter(Boolean);

  const usedProductIds = rows
    .filter((r) => r.scope === SCOPE_PRODUCT)
    .map((r) => r.productId)
    .filter(Boolean);

  return (
    <Card className={`${classes.card} border-left-green`} elevation={2}>
      <CardHeader
        title="Self-Inspect Minimum Image Count"
        classes={{ root: classes.cardHeader, title: classes.cardHeaderTitle }}
      />

      <CardContent className={classes.cardContent}>
        <TableContainer>
          <Table size="small" style={{ tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '22%' }} />
              <col style={{ width: '52%' }} />
              <col style={{ width: '16%' }} />
              <col style={{ width: '10%' }} />
            </colgroup>
            <TableHead>
              <TableRow>
                <TableCell>Scope</TableCell>
                <TableCell>Property Type / Product</TableCell>
                <TableCell align="center">Min Images</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No configurations. Add a row to get started.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row, index) => (
                <TableRow key={row._key}>
                  <TableCell>
                    <Select
                      value={row.scope}
                      onChange={(e) => handleScopeChange(index, e.target.value)}
                      variant="outlined"
                      margin="dense"
                      className={classes.scopeSelect}
                    >
                      <MenuItem value={SCOPE_PROPERTY_TYPE}>Property Type</MenuItem>
                      <MenuItem value={SCOPE_PRODUCT}>Product</MenuItem>
                    </Select>
                  </TableCell>

                  <TableCell>
                    {row.scope === SCOPE_PROPERTY_TYPE ? (
                      <Select
                        value={row.propertyTypeId}
                        onChange={(e) => update(index, { propertyTypeId: e.target.value })}
                        variant="outlined"
                        margin="dense"
                        displayEmpty
                        className={classes.valueSelect}
                      >
                        <MenuItem value="" disabled>Select property type...</MenuItem>
                        {propertyTypes
                          .filter(
                            (pt) =>
                              !usedPropertyTypeIds.includes(pt.propertyTypeId) ||
                              pt.propertyTypeId === row.propertyTypeId
                          )
                          .map((pt) => (
                            <MenuItem key={pt.propertyTypeId} value={pt.propertyTypeId}>
                              {pt.name}
                            </MenuItem>
                          ))}
                      </Select>
                    ) : (
                      <Select
                        value={row.productId}
                        onChange={(e) => update(index, { productId: e.target.value })}
                        variant="outlined"
                        margin="dense"
                        displayEmpty
                        className={classes.valueSelect}
                      >
                        <MenuItem value="" disabled>Select product...</MenuItem>
                        {products
                          .filter(
                            (p) =>
                              !usedProductIds.includes(p.productId) ||
                              p.productId === row.productId
                          )
                          .map((p) => (
                            <MenuItem key={p.productId} value={p.productId}>
                              {p.productCode} — {p.name}
                            </MenuItem>
                          ))}
                      </Select>
                    )}
                  </TableCell>

                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={row.minImageCount}
                      onChange={(e) => update(index, { minImageCount: e.target.value })}
                      inputProps={{ min: 1, style: { textAlign: 'center' } }}
                      variant="outlined"
                      size="small"
                      className={classes.numberInput}
                    />
                  </TableCell>

                  <TableCell align="right">
                    <Tooltip title="Remove">
                      <IconButton size="small" onClick={() => handleDelete(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>

      <CardActions className={classes.cardActions}>
        <Button size="small" startIcon={<AddIcon />} onClick={handleAddRow}>
          Add Rule
        </Button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {dirty && <Typography className={classes.unsaved}>● unsaved changes</Typography>}
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !dirty}
          >
            Save Changes
          </Button>
        </div>
      </CardActions>
    </Card>
  );
};
